import { useCallback, useEffect, useState } from 'react';
import { Content, Header, Link, Page, Progress, Table, TableColumn } from '@backstage/core-components';
import { discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@material-ui/core';
import HistoryIcon from '@material-ui/icons/History';
import RefreshIcon from '@material-ui/icons/Refresh';
import { canUseVersion, formatBytes, ImageRepository, ImageRow, summary, toRows } from './imagesFormat';

// Imagens guardadas no registry interno (somente leitura): o que o pipeline já
// publicou, tamanho, data e qual aplicação usa cada tag. Vem do backend do plugin
// images (rota /images), só para quem está no grupo admin, como Aplicações. "Usar esta
// versão" troca a imagem da aplicação por uma tag antiga (sem build), com confirmação.

function timeAgo(iso: string | undefined): string {
  const then = Date.parse(iso ?? '');
  if (Number.isNaN(then)) return '-';
  const minutes = Math.max(0, Math.round((Date.now() - then) / 60000));
  if (minutes < 1) return 'agora há pouco';
  if (minutes < 60) return `há ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `há ${hours} h`;
  const days = Math.round(hours / 24);
  return `há ${days} ${days === 1 ? 'dia' : 'dias'}`;
}

export const ImagesPage = () => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [repositories, setRepositories] = useState<ImageRepository[] | undefined>();
  const [loadError, setLoadError] = useState<string>();
  const [showCache, setShowCache] = useState(false);
  const [candidate, setCandidate] = useState<ImageRow>();
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string>();
  const [actionError, setActionError] = useState<string>();

  const load = useCallback(async () => {
    try {
      const baseUrl = await discoveryApi.getBaseUrl('images');
      const response = await fetchApi.fetch(`${baseUrl}/images`);
      const body = (await response.json().catch(() => ({}))) as {
        repositories?: ImageRepository[];
        error?: string;
      };
      if (!response.ok) {
        throw new Error(body.error ?? `Falha ao carregar as imagens (${response.status})`);
      }
      setRepositories(body.repositories ?? []);
      setLoadError(undefined);
    } catch (error) {
      setLoadError((error as Error).message);
    }
  }, [discoveryApi, fetchApi]);

  useEffect(() => {
    load();
  }, [load]);

  const useVersion = async () => {
    if (!candidate) return;
    setBusy(true);
    setActionError(undefined);
    setMessage(undefined);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('images');
      const response = await fetchApi.fetch(`${baseUrl}/images/use`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ repository: candidate.repository, tag: candidate.tag }),
      });
      const body = (await response.json().catch(() => ({}))) as { error?: string; message?: string };
      if (!response.ok) throw new Error(body.error ?? `Falha (${response.status})`);
      setMessage(`${candidate.app}: ${body.message}`);
      await load();
    } catch (error) {
      setActionError(`${candidate.app}: ${(error as Error).message}`);
    } finally {
      setBusy(false);
      setCandidate(undefined);
    }
  };

  const rows = toRows(repositories ?? [], showCache);

  const columns: TableColumn<ImageRow>[] = [
    { title: 'Repositório', field: 'repository' },
    {
      title: 'Versão',
      field: 'tag',
      render: row => (
        <span style={{ fontFamily: 'monospace' }} title={row.tag}>
          {row.commit ?? row.tag}
        </span>
      ),
    },
    { title: 'Tamanho', field: 'sizeBytes', render: row => formatBytes(row.sizeBytes) },
    {
      title: 'Publicada',
      field: 'createdAt',
      render: row => (
        <span title={row.createdAt ? new Date(row.createdAt).toLocaleString('pt-BR') : undefined}>
          {timeAgo(row.createdAt)}
        </span>
      ),
    },
    {
      title: 'Em uso por',
      field: 'usedBy',
      sorting: false,
      render: row =>
        row.usedBy.length ? (
          <>
            {row.usedBy.map(app => (
              <div key={app}>
                <Link to={`/catalog/default/component/${app}`}>{app}</Link>
              </div>
            ))}
          </>
        ) : (
          '-'
        ),
    },
    {
      title: 'Digest',
      field: 'digest',
      render: row =>
        row.digest ? (
          <span style={{ fontFamily: 'monospace' }} title={row.digest}>
            {row.digest.replace('sha256:', '').slice(0, 12)}
          </span>
        ) : (
          '-'
        ),
    },
    {
      title: 'Ações',
      field: 'tag',
      sorting: false,
      render: row => (
        <Tooltip
          title={
            canUseVersion(row)
              ? 'Usar esta versão'
              : row.usedBy.length
                ? 'Esta é a versão em uso'
                : 'Só tags de aplicações do pipeline'
          }
        >
          <span>
            <IconButton
              size="small"
              aria-label={`Usar a versão ${row.commit ?? row.tag} de ${row.repository}`}
              disabled={!canUseVersion(row) || busy}
              onClick={() => setCandidate(row)}
            >
              <HistoryIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      ),
    },
  ];

  return (
    <Page themeId="tool">
      <Header
        title="Imagens"
        subtitle="Imagens publicadas pelos pipelines no registry da plataforma: veja as versões e volte uma aplicação para uma delas"
      />
      <Content>
        {loadError && (
          <Typography color="error" style={{ marginBottom: 8 }}>
            {loadError}
          </Typography>
        )}
        {actionError && (
          <Typography color="error" style={{ marginBottom: 8 }}>
            {actionError}
          </Typography>
        )}
        {message && <Typography style={{ color: '#15803d', marginBottom: 8 }}>{message}</Typography>}
        {!repositories && !loadError && <Progress />}
        {repositories && rows.length === 0 && (
          <Paper style={{ padding: 24 }}>
            <Typography variant="h6" gutterBottom>
              Nenhuma imagem no registry ainda
            </Typography>
            <Typography variant="body2">
              A imagem aparece aqui depois do primeiro pipeline bem-sucedido (página{' '}
              <Link to="/pipelines">Pipelines</Link>). Um registry vazio num cluster novo é esperado: as
              imagens são reconstruídas pelo pipeline.
            </Typography>
          </Paper>
        )}
        {repositories && rows.length > 0 && (
          <Table
            title={summary(rows)}
            columns={columns}
            data={rows}
            options={{ search: true, paging: rows.length > 20, padding: 'dense' }}
            actions={[
              {
                icon: () => <RefreshIcon />,
                tooltip: 'Atualizar agora',
                isFreeAction: true,
                onClick: () => load(),
              },
            ]}
          />
        )}
        {repositories && (
          <FormControlLabel
            style={{ marginTop: 8 }}
            control={<Checkbox checked={showCache} onChange={event => setShowCache(event.target.checked)} />}
            label="Mostrar cache de build (kaniko-cache)"
          />
        )}
        <Dialog open={!!candidate} onClose={() => !busy && setCandidate(undefined)}>
          <DialogTitle>Usar a versão {candidate?.commit ?? candidate?.tag}?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              A aplicação <strong>{candidate?.app}</strong> passa a rodar esta versão (publicada{' '}
              {timeAgo(candidate?.createdAt)}), sem novo build. A versão atual sai do ar quando a nova ficar
              pronta.
            </DialogContentText>
            <DialogContentText>
              Vale até o próximo commit no repositório (ou "Executar agora" em Pipelines): aí o pipeline implanta
              o código mais novo de novo. Para voltar de vez, reverta o commit no git.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCandidate(undefined)} disabled={busy}>
              Cancelar
            </Button>
            <Button onClick={useVersion} color="primary" variant="contained" disabled={busy}>
              Usar esta versão
            </Button>
          </DialogActions>
        </Dialog>
      </Content>
    </Page>
  );
};
